import { DqTable } from "@decentriq/components";
import { format, isDate } from "date-fns";
import { type MRT_ColumnDef } from "material-react-table";
import { parse as parseCsv } from "papaparse";
import { memo, useCallback, useMemo } from "react";
import { useDebouncedSearch } from "hooks";
import DataRoomLogsToolbar from "./DataRoomLogsToolbar/DataRoomLogsToolbar";

enum AuditLogRecordType {
  StopDataRoomRequest = "StopDataRoomRequest",
  RetrieveAuditLogRequest = "RetrieveAuditLogRequest",
  ReturningQueryResults = "ReturningQueryResults",
  SqlQueryRequest = "SqlQueryRequest",
  CreateDataRoomRequest = "CreateDataRoomRequest",
  PublishDatasetToDataRoomRequest = "PublishDatasetToDataRoomRequest",
}

const auditLogRecordPriority: AuditLogRecordType[] = [
  AuditLogRecordType.CreateDataRoomRequest,
  AuditLogRecordType.PublishDatasetToDataRoomRequest,
  AuditLogRecordType.SqlQueryRequest,
  AuditLogRecordType.ReturningQueryResults,
  AuditLogRecordType.StopDataRoomRequest,
  AuditLogRecordType.RetrieveAuditLogRequest,
];

interface DataRoomLogsPreviewProps {
  logs?: string;
  asTable?: boolean;
  onFetchLogs: () => void;
  isLoading?: boolean;
}

const DataRoomLogsPreview: React.FC<DataRoomLogsPreviewProps> = memo(
  ({ logs, asTable = false, onFetchLogs, isLoading }) => {
    const { debouncedSearch, onSearchChange, searchValue } =
      useDebouncedSearch();
    const logsData = useMemo<{ data: string[][]; rowLength: number }>(() => {
      if (asTable && logs) {
        const { data, errors } = parseCsv<string[]>(logs);
        if (!errors.length) {
          return {
            data: data
              .filter((r) => r.some((e) => e))
              .map((r) => {
                const timestamp = r[0];
                const currentTimestamp = Date.now().toString();
                const date = new Date(
                  Number(timestamp) *
                    (currentTimestamp.length === timestamp.length ? 1 : 1000)
                );
                if (isDate(date)) {
                  r[0] = date.getTime().toString();
                } else {
                  r[0] = "";
                }
                return r;
              })
              // TODO: remove sorting when that will be implemented in enclave
              .sort((a, b) => {
                const diff = Number(b[0]) - Number(a[0]);
                if (diff === 0) {
                  const aType = a[3];
                  const bType = b[3];
                  return (
                    auditLogRecordPriority.indexOf(
                      bType as AuditLogRecordType
                    ) -
                    auditLogRecordPriority.indexOf(aType as AuditLogRecordType)
                  );
                }
                return diff;
              }),
            rowLength: 3,
          };
        }
        return { data: [], rowLength: 0 };
      }
      return { data: [], rowLength: 0 };
    }, [logs, asTable]);
    const data = logsData.data as [string, string, string][];
    const filteredData = useMemo(() => {
      return data.filter((row) => {
        return row[2]
          .toLowerCase()
          .trim()
          .includes(debouncedSearch.toLowerCase().trim());
      });
    }, [data, debouncedSearch]);

    const getLogs = useCallback(() => {
      const data = logsData.data;
      return data.reverse().map((log) => {
        const formattedDate = format(
          new Date(Number(log[0])),
          "HH:mm:ss dd-MM-yyyy"
        );
        return [formattedDate, log[1], log[2]];
      });
    }, [logsData.data]);
    const columns: MRT_ColumnDef<[string, string, string]>[] = [
      {
        Cell: ({ renderedCellValue: value }) =>
          value
            ? format(new Date(Number(value)), "HH:mm:ss dd-MM-yyyy")
            : "Invalid date",
        accessorFn: (row) => row[0],
        grow: false,
        header: "Event date",
        id: "timestamp",
        size: 170,
      },
      {
        accessorFn: (row) => row[1],
        grow: false,
        header: "Performed by",
        id: "user",
        size: 300,
      },
      {
        accessorFn: (row) => row[2],
        header: "Description",
        id: "event",
      },
    ];
    return (
      <DqTable
        columns={columns}
        data={filteredData}
        enableGlobalFilter={false}
        enableTopToolbar={true}
        initialState={{
          showGlobalFilter: true,
        }}
        muiTableHeadRowProps={{
          sx: {
            "& > .MuiTableCell-root:first-child": { pl: 2 },
            "& > .MuiTableCell-root:last-child": {
              "& .Mui-TableHeadCell-ResizeHandle-Wrapper": {
                right: "-1rem",
              },
              pr: 2,
            },
          },
        }}
        muiTablePaperProps={{
          sx: {
            display: "flex",
            flex: 1,
            flexDirection: "column",
            height: "100%",
            overflow: "hidden",
            width: "100%",
          },
        }}
        renderTopToolbarCustomActions={() => {
          return (
            <DataRoomLogsToolbar
              getLogs={getLogs}
              hasData={filteredData.length > 0}
              isLoading={isLoading}
              onFetchLogs={onFetchLogs}
              onSearchChange={onSearchChange}
              searchValue={searchValue}
            />
          );
        }}
      />
    );
  }
);
DataRoomLogsPreview.displayName = "DataRoomLogsPreview";

export default DataRoomLogsPreview;
